import React, { useState, useEffect } from 'react';
import { authService } from '@/services/authService'; 
import LoadingIndicator from './LoadingIndicator';
import { LogInIcon, UserPlusIcon, AlertTriangleIcon, MailIcon, LockIcon, EyeIcon, EyeOffIcon, ZapIcon } from './icons';
import { reliableImageService } from '../services/reliableImageService';

interface AuthScreenProps {
  onAuthSuccess: () => void;
}

const AuthScreen: React.FC<AuthScreenProps> = ({ onAuthSuccess }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [backgroundImage, setBackgroundImage] = useState<string>('');

  // Load background image on component mount
  useEffect(() => {
    const loadBackgroundImage = async () => {
      try {
        const imageUrl = await reliableImageService.generateForScreen('login');
        setBackgroundImage(imageUrl);
      } catch (error) {
        console.error('Failed to load login background:', error);
        // Use default fallback
        setBackgroundImage('https://image.pollinations.ai/prompt/cyberpunk%20city%20at%20night%2C%20neon%20lights%2C%20rain%2C%20futuristic%20buildings%2C%20dark%20atmosphere%2C%20digital%20art?width=1920&height=1080&seed=12345&nologo=true');
      }
    };

    loadBackgroundImage();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    if (!email.trim() || !password.trim()) {
        setError("Email and password cannot be empty.");
        setIsLoading(false);
        return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
        setError("Please enter a valid email address.");
        setIsLoading(false);
        return;
    }
    if (!isLogin && password.length < 6) {
        setError("Password must be at least 6 characters long for sign up.");
        setIsLoading(false);
        return;
    }

    // 🎮 DEMO ACCOUNT: Skip authentication for testing
    if (email === 'demo@lucidcyber.com' && password === 'demo123') {
      console.log('🎮 [DEMO] Using demo account - creating demo session');
      
      // Create a demo user session
      const demoUser = {
        id: 'demo-user-id',
        email: 'demo@lucidcyber.com',
        user_metadata: {
          display_name: 'Demo Player',
          email: 'demo@lucidcyber.com'
        }
      };
      
      // Trigger the auth success callback
      setIsLoading(false);
      onAuthSuccess();
      
      // Dispatch a custom event to simulate auth state change
      window.dispatchEvent(new CustomEvent('demo-auth-success', { 
        detail: { user: demoUser } 
      }));
      
      return;
    }

    // 🔧 DEBUG: Test OpenRouter story generation when in debug mode
    if (email === 'debug@test.com' && password === 'debug123') {
      console.log('🧪 [DEBUG] Testing OpenRouter story generation...');
      try {
        const { generateStoryContent } = await import('../services/openRouterService');
        
        const prompt = "You wake up in a dim alley behind a neon-lit bar in the Data District of Neo-Veridia...";
        const archetype = {
          id: 'hacker',
          name: 'Hacker',
          description: 'Tech-savvy infiltrator who navigates digital and physical realms',
          initialPromptSuffix: ''
        };
        const userProfile = { name: 'TestPlayer', age: 25, traits: [], mindset: { name: 'Pragmatic', description: 'Debug mode' } };
        const storyHistory: any[] = [];
        const userId = 'debug-test-user';

        const result = await generateStoryContent(prompt, storyHistory, archetype, userProfile, userId);
        console.log('✅ [DEBUG] OpenRouter test successful!', result);
        setError('DEBUG: OpenRouter test completed - check console for results');
        setIsLoading(false);
        return;
      } catch (debugError) {
        console.error('❌ [DEBUG] OpenRouter test failed:', debugError);
        setError(`DEBUG: OpenRouter test failed - ${debugError instanceof Error ? debugError.message : String(debugError)}`);
        setIsLoading(false);
        return;
      }
    }

    try {
      if (isLogin) {
        console.log('[Auth] Attempting sign in...');
        const result = await authService.signIn(email, password);
        console.log('[Auth] Sign in result:', result);
        onAuthSuccess();
      } else {
        console.log('[Auth] Attempting sign up...');
        const result = await authService.signUp(email, password);
        console.log('[Auth] Sign up result:', result);
        
        // For sign up, we might need to handle email confirmation
        if (result.user && !result.session) {
          setError('Please check your email for a confirmation link, then try signing in.');
          setIsLogin(true); // Switch to login mode
        } else {
          onAuthSuccess();
        }
      }
    } catch (err) {
      console.error('Auth error:', err);
      let errorMessage = 'Authentication failed. Please check your credentials or network connection.';
      if (err instanceof Error) {
        // Use the improved error messages from supabaseService
        errorMessage = err.message;
        
        // Additional client-side error handling
        if (err.message.includes('Network')) {
            errorMessage = 'Network error. Please check your connection and try again.';
        } else if (err.message.includes('rate limit')) {
            errorMessage = 'Too many attempts. Please wait a moment and try again.';
        }
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-purple-900 to-slate-950">
        <LoadingIndicator message={isLogin ? "Authenticating..." : "Initializing Profile..."} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-900 to-slate-950 text-slate-200 flex items-center justify-center p-4 font-sans relative overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: backgroundImage ? `url('${backgroundImage}')` : `url('https://image.pollinations.ai/prompt/Cyberpunk%20city%20at%20night%2C%20neon%20lights%2C%20rain%2C%20futuristic%20buildings%2C%20dark%20atmosphere%2C%20digital%20art?width=1920&height=1080&seed=12345&nologo=true&enhance=true')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.3
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950/80 via-purple-900/80 to-slate-950/80 z-10" />
      
      <div className="bg-slate-900/70 backdrop-blur-lg p-8 rounded-xl shadow-2xl border border-fuchsia-500/30 w-full max-w-md transform transition-all duration-500 ease-in-out relative z-20">
        <div className="text-center mb-8">
          <ZapIcon className="w-16 h-16 mx-auto mb-4 text-fuchsia-400 animate-pulse" />
          <h1 className="text-4xl font-orbitron text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-400 to-cyan-400 mb-2">
            Lucid Link
          </h1>
          <p className="text-slate-400 text-sm">Connect to the Cybernetic Dreamscape</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-1">
              Neural ID (Email)
            </label>
            <div className="relative">
              <MailIcon className="w-5 h-5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="email"
                id="email"
                data-testid="auth-email-input"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(null); }}
                required
                className="w-full pl-10 pr-4 py-3 bg-slate-800/60 border border-slate-700 rounded-lg 
                           text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 
                           focus:ring-fuchsia-500 focus:border-fuchsia-500 transition-colors"
                placeholder="your.id@network.edge"
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-1">
              Access Key (Password)
            </label>
            <div className="relative">
              <LockIcon className="w-5 h-5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                data-testid="auth-password-input"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(null); }}
                required
                minLength={isLogin ? undefined : 6} 
                className="w-full pl-10 pr-10 py-3 bg-slate-800/60 border border-slate-700 rounded-lg 
                           text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 
                           focus:ring-fuchsia-500 focus:border-fuchsia-500 transition-colors"
                placeholder="Enter secure passphrase"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-fuchsia-400 transition-colors p-1"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOffIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-800/40 border border-red-600/50 rounded-lg text-red-200 text-sm flex items-center gap-2 animate-shake">
              <AlertTriangleIcon className="w-5 h-5 flex-shrink-0" /> 
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            data-testid={isLogin ? "auth-signin-button" : "auth-signup-button"}
            disabled={isLoading}
            className="w-full py-3 px-4 bg-gradient-to-r from-fuchsia-600 via-purple-600 to-cyan-600 
                       hover:from-fuchsia-500 hover:via-purple-500 hover:to-cyan-500 text-white font-semibold 
                       rounded-lg shadow-lg hover:shadow-fuchsia-500/30 transform transition-all duration-300 
                       hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-fuchsia-500
                       disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
          >
            {isLoading 
              ? (isLogin ? 'Connecting...' : 'Initializing...') 
              : (isLogin ? <><LogInIcon className="inline w-5 h-5 mr-2"/>Jack In</> : <><UserPlusIcon className="inline w-5 h-5 mr-2"/>Initialize Profile</>) 
            }
          </button>
        </form>

        <div className="text-center mt-8">
          <button
            type="button"
            data-testid="auth-toggle-mode"
            onClick={() => {
              setIsLogin(!isLogin);
              setError(null);
            }}
            className="text-sm text-slate-400 hover:text-fuchsia-400 transition-colors duration-200 group"
          >
            {isLogin 
              ? <>Need a new Neural ID? <span className="font-semibold group-hover:underline">Create Profile</span></>
              : <>Already connected? <span className="font-semibold group-hover:underline">Sign In</span></>
            }
          </button>
        </div>

        <div className="text-center mt-6 text-xs text-slate-600">
          <div className="mb-4 p-3 bg-cyan-900/20 border border-cyan-500/30 rounded-lg">
            <p className="text-cyan-300 font-semibold mb-1">🎮 Demo Access</p>
            <p className="text-slate-400">Email: demo@lucidcyber.com</p>
            <p className="text-slate-400">Password: demo123</p>
          </div>
          <p>&copy; {new Date().getFullYear()} Lucid Cyber Systems. All rights reserved.</p>
          <p>Neural interface secured by Supabase Quantum Encryption.</p>
        </div>
      </div>
    </div>
  );
};

export default AuthScreen;
